import React from "react";
import {
  styled,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  TableContainer as MuiTableContainer,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import InboxIcon from "@mui/icons-material/Inbox";

export type StatusVariant =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "default";

// Surface, density and hairlines all come from the theme's
// MuiTable* overrides - see shared/styles/theme.ts. These wrappers
// only add what is specific to a data grid.

/** Bordered surface the table sits on. */
export const TableContainer = styled(MuiTableContainer)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  overflow: "auto",
}));

// No minimum width: these tables are used both for wide data grids,
// which sit in a scrolling container that handles their own width, and
// for narrow key/value panels inside cards, where forcing a minimum
// pushed the value column outside the card and clipped it.
export const Table = MuiTable;

export const TableHead = MuiTableHead;

export const TableBody = MuiTableBody;

export const TableCell = MuiTableCell;

/** Row with hover feedback; `highlight` pins the emphasised state on. */
export const TableRow = styled(MuiTableRow, {
  shouldForwardProp: (prop) => prop !== "highlight",
})<{ highlight?: boolean }>(({ theme, highlight = false }) => ({
  transition: "background-color 0.15s ease",
  "&:hover": {
    backgroundColor: theme.palette.surface.hover,
  },
  ...(highlight && {
    backgroundColor: theme.palette.slate.highlight,
    "&:hover": {
      backgroundColor: theme.palette.slate.highlight,
    },
  }),
}));

/** Full-width placeholder for an empty result set. */
export const EmptyState = ({
  message,
  action,
  colSpan = 3,
}: {
  message: string;
  action?: React.ReactNode;
  colSpan?: number;
}) => (
  <MuiTableRow>
    <MuiTableCell colSpan={colSpan} sx={{ border: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          py: 7,
          px: 3,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "surface.inset",
            color: "text.disabled",
          }}
        >
          <InboxIcon sx={{ fontSize: "1.375rem" }} />
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {message}
        </Typography>
        {action}
      </Box>
    </MuiTableCell>
  </MuiTableRow>
);

/** Full-width loading placeholder that keeps the table height stable. */
export const LoadingRow = ({ colSpan = 100 }: { colSpan?: number }) => (
  <MuiTableRow>
    <MuiTableCell colSpan={colSpan} sx={{ border: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          py: 7,
        }}
      >
        <CircularProgress size={18} thickness={5} color="inherit" sx={{ color: "text.disabled" }} />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Lade…
        </Typography>
      </Box>
    </MuiTableCell>
  </MuiTableRow>
);

/** Pill status marker. Fill, text and border are derived per variant. */
const StatusBadgeRoot = styled("span", {
  shouldForwardProp: (prop) => prop !== "variant",
})<{ variant: StatusVariant }>(({ theme, variant }) => {
  const mode = theme.palette.mode;

  const variants: Record<StatusVariant, React.CSSProperties> = {
    success: {
      backgroundColor: theme.palette.overlay.success,
      color: theme.palette.success.main,
      borderColor: theme.palette.statusBorder.success[mode],
    },
    warning: {
      backgroundColor: theme.palette.overlay.warning,
      color: theme.palette.warning.main,
      borderColor: theme.palette.statusBorder.warning[mode],
    },
    error: {
      backgroundColor: theme.palette.overlay.error,
      color: theme.palette.error.main,
      borderColor: theme.palette.statusBorder.error[mode],
    },
    info: {
      backgroundColor: theme.palette.overlay.info,
      color: theme.palette.info.main,
      borderColor: theme.palette.statusBorder.info[mode],
    },
    default: {
      backgroundColor: theme.palette.statusBadge.default[mode].bg,
      color: theme.palette.text.secondary,
      borderColor: theme.palette.statusBadge.default[mode].border,
    },
  };

  return {
    display: "inline-flex",
    alignItems: "center",
    whiteSpace: "nowrap",
    padding: "3px 10px",
    borderRadius: 8,
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1.5,
    border: "1px solid",
    ...variants[variant],
  };
});

export const StatusBadge = ({
  status,
  variant = "default",
}: {
  status: React.ReactNode;
  variant?: StatusVariant;
}) => <StatusBadgeRoot variant={variant}>{status}</StatusBadgeRoot>;

/** Right-aligned container for per-row controls. */
export const TableActions = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.25,
      justifyContent: "flex-end",
    }}
  >
    {children}
  </Box>
);

const ACTION_SIZES = { sm: 28, md: 32, lg: 38 } as const;

/** Compact icon control used inside table rows and toolbars. */
export const ActionButton = ({
  icon,
  onClick,
  label,
  variant = "ghost",
  size = "md",
}: {
  icon: React.ReactNode;
  onClick?: () => void;
  label?: string;
  variant?: "ghost" | "contained" | "outlined";
  size?: "sm" | "md" | "lg";
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    sx={(theme) => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: ACTION_SIZES[size],
      height: ACTION_SIZES[size],
      flexShrink: 0,
      borderRadius: 8,
      cursor: "pointer",
      transition: "background-color 0.15s ease, color 0.15s ease",
      "& .MuiSvgIcon-root": { fontSize: size === "lg" ? "1.25rem" : "1.125rem" },
      ...(variant === "ghost" && {
        border: "none",
        backgroundColor: "transparent",
        color: theme.palette.text.secondary,
        "&:hover": {
          backgroundColor: theme.palette.surface.hover,
          color: theme.palette.text.primary,
        },
      }),
      ...(variant === "outlined" && {
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.secondary,
        "&:hover": {
          backgroundColor: theme.palette.surface.hover,
          color: theme.palette.text.primary,
        },
      }),
      ...(variant === "contained" && {
        border: "none",
        backgroundColor: theme.palette.slate.active,
        color: theme.palette.text.primary,
        "&:hover": { backgroundColor: theme.palette.actionColors.contained },
      }),
    })}
  >
    {icon}
  </Box>
);

/**
 * Compact metric tile for the dashboard. Passing `onClick` makes it a
 * real button - hoverable, focusable and keyboard-activatable - rather
 * than a div with a click handler.
 */
export const QuickStatsCard = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  trendUp = true,
  onClick,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: string;
  trendUp?: boolean;
  onClick?: () => void;
}) => (
  <Box
    {...(onClick && { component: "button", type: "button", onClick })}
    sx={(theme) => ({
      display: "block",
      width: "100%",
      textAlign: "left",
      font: "inherit",
      color: "inherit",
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 3,
      p: 2,
      height: "100%",
      ...(onClick && {
        cursor: "pointer",
        transition: "background-color 0.15s ease, border-color 0.15s ease",
        "&:hover": {
          // A solid step up from `paper`, not a translucent overlay -
          // an overlay on this surface composites to almost the same
          // value and reads as no feedback at all.
          backgroundColor: theme.palette.background.subtle,
          borderColor: theme.palette.primary.main,
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }),
    })}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 2,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "quickStats.iconBg",
          color: "text.secondary",
          "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "1.375rem",
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: "-0.02em",
          }}
        >
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {title}
        </Typography>
      </Box>

      {trend && (
        <Box sx={{ ml: "auto", flexShrink: 0 }}>
          <StatusBadge
            status={`${trendUp ? "+" : "−"}${trend}`}
            variant={trendUp ? "success" : "error"}
          />
        </Box>
      )}
    </Box>

    {subtitle && (
      <Typography
        variant="caption"
        sx={{ display: "block", mt: 1, color: "text.disabled" }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);
